// identity.js — M3 (WAVY_AUDIT.md): server-authoritative dashboard identity.
//
// GET /api/users/me resolves the signed-in user's appointment (role, branch,
// subBranch) from the DATABASE via the verified JWT. localStorage role/branch
// values are display/session cache only and must never decide which dashboard
// a user lands on — a stale or tampered localStorage profile could otherwise
// route the user to the wrong desk.
//
// fetchMyIdentity(): module-level cached fetch (one request per app session,
// in-flight dedupe, fail-soft: returns null on failure so callers keep their
// token/localStorage fallback behaviour).
// useMyIdentity(): React hook returning { identity, resolved }.
import { useEffect, useState } from "react";
import { getApiBaseUrl } from "./baseUrl";

let cachedIdentity = null;
let inFlight = null;

export const fetchMyIdentity = async (force = false) => {
  if (!force && cachedIdentity) return cachedIdentity;
  if (!force && inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return null;
      const res = await fetch(`${getApiBaseUrl()}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => ({}));
      cachedIdentity = data?.identity || null;
      return cachedIdentity;
    } catch {
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

export const clearIdentityCache = () => {
  cachedIdentity = null;
};

export const useMyIdentity = () => {
  const [identity, setIdentity] = useState(cachedIdentity);
  const [resolved, setResolved] = useState(Boolean(cachedIdentity));

  useEffect(() => {
    let cancelled = false;
    fetchMyIdentity().then((result) => {
      if (!cancelled) {
        setIdentity(result);
        setResolved(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { identity, resolved };
};

export default { fetchMyIdentity, clearIdentityCache, useMyIdentity };
